import { useContext, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemButton,
    Box,
    Typography,
    Divider,
    IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../AppContext';
import { SttList } from './SttList';

type Terrain = {
    id: number;
    name: string;
};

type TerrainsResponse = {
    rows?: Terrain[];
};

type Props = {
    terrains: Terrain[];
    onCreate: (terrain: Record<string, never>) => void;
    onAdd: (terrain: Terrain) => void;
    onDelete?: (terrain: Terrain) => void;
};

export function SttTerrainList({
    terrains,
    onCreate,
    onAdd,
    onDelete,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [selectionOpen, setSelectionOpen] = useState(false);
    const [allTerrains, setAllTerrains] = useState<Terrain[]>();

    function openAddTerrain() {
        setSelectionOpen(true);
    }

    /**
     * Load all terrains
     */
    useEffect(() => {
        // Fetch all terrains on the sports center
        if (selectionOpen && !allTerrains) {
            cxt?.api('GET', `/scs/${cxt?.sc?.id}/terrains`, {
                params: { rows: 1000 },
                success: (response: TerrainsResponse) => {
                    if (response.rows) {
                        setAllTerrains(response.rows);
                    }
                },
            });
        }
    }, [selectionOpen, allTerrains, cxt]);

    function selectTerrain(terrain: Terrain) {
        setSelectionOpen(false);
        onAdd(terrain);
    }

    function createTerrain(terrain: Record<string, never>) {
        setSelectionOpen(false);
        onCreate(terrain);
    }

    // Filter terrains by culling from 'allTerrains' those already present in 'terrains'
    const existingTerrains = allTerrains
        ? allTerrains.filter(
              (terrain) => !terrains.find((tr: any) => tr.id === terrain.id),
          )
        : [];

    // -----| Render |-----
    return (
        <>
            <SttList
                title={t('terrain.terrains')}
                onAdd={openAddTerrain}
                onDelete={onDelete}
                data={
                    terrains &&
                    terrains.map((terrain: any) => ({
                        caption: terrain.name,
                        objToSelect: terrain,
                    }))
                }
            />

            <Dialog
                onClose={() => setSelectionOpen(false)}
                open={selectionOpen}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Typography variant="h6" component="span" fontWeight={600}>
                        {t('SelectTerrain')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={() => createTerrain({})}
                            color="primary"
                            title={t('terrain.newTerrain')}
                        >
                            <AddIcon />
                        </IconButton>
                        <IconButton
                            onClick={() => setSelectionOpen(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <List disablePadding>
                        {existingTerrains.length > 0 ? (
                            existingTerrains.map((terrain: any) => (
                                <ListItem key={terrain.id} disablePadding>
                                    <ListItemButton
                                        onClick={() => selectTerrain(terrain)}
                                    >
                                        <ListItemIcon>
                                            <PlaceIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={terrain.name} />
                                    </ListItemButton>
                                </ListItem>
                            ))
                        ) : (
                            <Box py={4} textAlign="center">
                                <Typography color="text.secondary">
                                    {t('NoResults')}
                                </Typography>
                            </Box>
                        )}
                    </List>
                </DialogContent>
            </Dialog>
        </>
    );
}
